import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, ApiError } from "@/lib/api";
import { applyFolderMove, pendingFolder, proposeFolderName } from "@/lib/agent-folders";
import { qk } from "@/lib/query/keys";
import type { AgentFolder, AgentWithContext } from "@/lib/types";

const selectFolders = (data: { folders: AgentFolder[] }) => data.folders;
const NO_FOLDERS: AgentFolder[] = [];

type AgentsData = { agents: AgentWithContext[] };
type FoldersData = { folders: AgentFolder[] };

/** The Agents list's folders, from the cache; under the `agents` family, so the live feed keeps them current. */
export function useAgentFolders(): AgentFolder[] {
  const query = useQuery({ queryKey: qk.agentFolders(), queryFn: api.listAgentFolders, select: selectFolders });
  return query.data ?? NO_FOLDERS;
}

/** Asks the list to open a folder's name for editing — after a drop makes one, or "New folder…" in a menu. */
export const FOLDER_RENAME_EVENT = "agent-folder:rename";
const requestRename = (folderId: string) => window.dispatchEvent(new CustomEvent<string>(FOLDER_RENAME_EVENT, { detail: folderId }));

type FolderOp =
  | { kind: "move"; agentId: string; folderId: string | null }
  | { kind: "create"; agentIds: string[]; pending: AgentFolder }
  | { kind: "rename"; folderId: string; name: string }
  | { kind: "ungroup"; folderId: string };

/**
 * Arranging the folders, each optimistic: the cache changes at once — the agent
 * moves, an emptied folder goes — and is put back if the server refuses. Made
 * with `useMutation` so the live feed holds its refetch until the server has
 * answered, rather than drawing the old arrangement for a moment in between.
 */
export function useFolderActions() {
  const client = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (op: FolderOp): Promise<AgentFolder | null> => {
      if (op.kind === "move") await api.updateAgent(op.agentId, { folderId: op.folderId });
      else if (op.kind === "create") return api.createAgentFolder({ agentIds: op.agentIds });
      else if (op.kind === "rename") await api.renameAgentFolder(op.folderId, op.name);
      else await api.ungroupAgentFolder(op.folderId);
      return null;
    },
    onMutate: async (op) => {
      await client.cancelQueries({ queryKey: qk.agents() });
      const agents = client.getQueryData<AgentsData>(qk.agents());
      const folders = client.getQueryData<FoldersData>(qk.agentFolders());
      if (agents && folders) {
        let next: { agents: AgentWithContext[]; folders: AgentFolder[] } = { agents: agents.agents, folders: folders.folders };
        if (op.kind === "move") next = applyFolderMove(agents.agents, folders.folders, [op.agentId], op.folderId);
        else if (op.kind === "create") next = applyFolderMove(agents.agents, folders.folders, op.agentIds, op.pending.id, op.pending);
        else if (op.kind === "rename") {
          next.folders = folders.folders.map((folder) => (folder.id === op.folderId ? { ...folder, name: op.name } : folder));
        } else {
          const members = agents.agents.filter((agent) => agent.folderId === op.folderId).map((agent) => agent.id);
          next = applyFolderMove(agents.agents, folders.folders, members, null);
        }
        client.setQueryData<AgentsData>(qk.agents(), { ...agents, agents: next.agents });
        client.setQueryData<FoldersData>(qk.agentFolders(), { ...folders, folders: next.folders });
      }
      return { agents, folders };
    },
    onSuccess: (made, op) => {
      if (op.kind !== "create" || !made) return;
      // The real folder takes the placeholder's place, then its name opens for editing.
      client.setQueryData<FoldersData>(qk.agentFolders(), (data) =>
        data ? { ...data, folders: data.folders.map((folder) => (folder.id === op.pending.id ? made : folder)) } : data,
      );
      client.setQueryData<AgentsData>(qk.agents(), (data) =>
        data
          ? { ...data, agents: data.agents.map((agent) => (agent.folderId === op.pending.id ? { ...agent, folderId: made.id } : agent)) }
          : data,
      );
      requestRename(made.id);
    },
    onError: (cause, op, context) => {
      if (context?.agents) client.setQueryData(qk.agents(), context.agents);
      if (context?.folders) client.setQueryData(qk.agentFolders(), context.folders);
      const what = { move: "move the agent", create: "make the folder", rename: "rename the folder", ungroup: "ungroup the folder" }[op.kind];
      toast.error(cause instanceof ApiError ? cause.message : `Could not ${what}`);
    },
    onSettled: () => client.invalidateQueries({ queryKey: qk.agents() }),
  });

  const { mutate } = mutation;
  const moveTo = useCallback((agentId: string, folderId: string | null) => mutate({ kind: "move", agentId, folderId }), [mutate]);
  /** A folder of these agents, named as the server will name it (`proposeFolderName`). */
  const createWith = useCallback(
    (agents: Pick<AgentWithContext, "id" | "handle" | "project">[]) => {
      const folders = client.getQueryData<FoldersData>(qk.agentFolders())?.folders ?? [];
      const name = proposeFolderName([
        agents.map((agent) => agent.project?.slug),
        agents.map((agent) => agent.project?.name),
        agents.map((agent) => agent.handle),
      ]);
      mutate({ kind: "create", agentIds: agents.map((agent) => agent.id), pending: pendingFolder(folders, name) });
    },
    [client, mutate],
  );
  const rename = useCallback((folderId: string, name: string) => mutate({ kind: "rename", folderId, name }), [mutate]);
  const ungroup = useCallback((folderId: string) => mutate({ kind: "ungroup", folderId }), [mutate]);
  return { moveTo, createWith, rename, ungroup, pending: mutation.isPending };
}
